import type { StrictPaddingStyle } from "../util/types";

import { Box } from "../Box";
import { Star } from "../icons";
import { useTheme } from "../ThemeProvider";

import React from "react";
import { View } from "react-native";
import Svg, { Path } from "react-native-svg";

export type StarRatingSize = "lg" | "md" | "sm";

const SIZE_MAP = {
  sm: 18,
  md: 20,
  lg: 24,
} as const;

const ICON_SIZE_MAP = {
  // Star sizes do not match the icon sizes of the same name.
  sm: "xs",
  md: "sm",
  lg: "md",
} as const;

interface StarRatingProps {
  /**
   * For use on inverse backgrounds.
   */
  inverse?: boolean;

  /**
   * Size of Star Rating Component, defaults to 'md'
   */
  size?: StarRatingSize;

  /**
   *  Rating value to display out of 5
   */
  value: number;

  /**
   * Accessibility label for screen readers
   */
  ariaLabel: string;

  /**
   * Defines the padding style overrides.
   */
  sx?: StrictPaddingStyle;
}

/**
 * @see https://experience.indeed.design/components/ids/star-rating
 */
export function StarRating({
  inverse = false,
  size = "md",
  value,
  ariaLabel,
  sx,
}: StarRatingProps) {
  const theme = useTheme();
  if (value < 0 || value > 5) {
    console.error("Rating must be between 0 and 5");
    return null;
  }
  const color = inverse ? theme.colors.foregroundInverse : theme.colors.foregroundStrong;
  const height = SIZE_MAP[size];
  const width = height * 5;

  const filledStarsNumber = Math.floor(value);
  const fractionalPart = value % 1;

  const stars: number[] = Array<number>(filledStarsNumber).fill(100);

  if (fractionalPart > 0 && filledStarsNumber < 5) {
    // Map the fractional part into the visible-fill range [20, 80].
    // The stroke star already covers the leftmost ~20% and
    // rightmost ~20% of its bounding box with stroke pixels, so a
    // literal `fractionalPart * 100` clip reads wrong at the
    // extremes — e.g. value=0.1 would clip at 10% and disappear
    // entirely behind the left stroke band, while 0.9 would clip at
    // 90% and read as visually full. Mapping into [20, 80] (so
    // 0.1 → 26%, 0.9 → 74%) makes the clipper match what a human
    // eyeballs.
    const percentageFilled = Math.floor(fractionalPart * 60 + 20);
    stars.push(percentageFilled);
  }
  while (stars.length < 5) {
    stars.push(0);
  }

  return (
    <Box
      sx={{
        flexDirection: "row",
        flexWrap: "nowrap",
        alignItems: "center",
        position: "relative",
        width: width,
        height: height,
        ...sx,
      }}
      role="img"
      aria-label={ariaLabel}
      accessible={true}
    >
      {stars.map((value, index) => (
        <PercentStar size={size} percentageFilled={value} color={color} key={`star-${index}`} />
      ))}
    </Box>
  );
}

interface PercentStarProps {
  size: StarRatingSize;
  percentageFilled: number;
  color: string;
}

const PercentStar: React.FC<PercentStarProps> = ({ size, percentageFilled, color }) => {
  const iconSize = SIZE_MAP[size];

  return (
    <View style={{ height: iconSize, width: iconSize }} aria-hidden={true}>
      <Box sx={{ position: "absolute", width: `${percentageFilled}%`, overflow: "hidden" }}>
        <Svg width={iconSize} height={iconSize} viewBox="0 0 24 24" aria-hidden>
          <Path
            d="M12 18.698l6.125 3.696a.593.593 0 00.883-.642l-1.625-6.967 5.412-4.688a.593.593 0 00-.339-1.04l-7.124-.604-2.786-6.573a.593.593 0 00-1.092 0L8.668 8.453l-7.124.605a.593.593 0 00-.339 1.039l5.412 4.688-1.625 6.967c-.12.51.435.913.884.642L12 18.698z"
            fill={color}
          />
        </Svg>
      </Box>
      <Star fill={color} variant="stroke" stroke={color} size={ICON_SIZE_MAP[size]} />
    </View>
  );
};
